import { useLocation } from '@reach/router';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  APIResponse,
  configuredFetch,
  defaultMetaQueryFn,
  defaultMutationFn,
} from 'api';
import { useVendor } from 'hooks';
import {
  ApiResponse,
  Attachment,
  Checklist,
  HistoryItem,
  NotesByDate,
  PaginatedApiResponse,
  PresignedFileUpload,
  Recall,
  TaskStatus,
  VehicleCard,
  VehicleImage,
  VehicleImageShots,
  VehicleRepairOrder,
  VehicleSummary,
} from 'models';
import { CheckListInstance } from 'models/inventory/checklist';
import { CreateVehiclePayload } from 'models/inventory/CreateVehicle';
import InventoryLocation from 'models/inventory/location';
import { VehicleAttachment } from 'models/inventory/vehicleAttachment';
import { VehicleTag } from 'models/inventory/vehicleTag';

import {
  getAllQueryParamsWithName,
  getLastQueryParamWithName,
} from '../../navigation/util/ParamHelpers';

export const INVENTORY_HISTORY_PAGE_SIZE = 50;
export const TASKS_PAGE_SIZE = 50;

/**
 * @package api/useVehicle
 * @description - Get history as HistoryItem[] for specific inventory
 * @param id vehicle UUID
 */
interface InventoryHistory {
  items: HistoryItem[];
}

export function useVehicleHistory(
  id: string,
  startIndex: number = 0,
  pageSize: number = INVENTORY_HISTORY_PAGE_SIZE
) {
  const queryKey = [`/inventory/${id}/history`, startIndex, pageSize];

  const defaultParams = { startIndex, pageSize };
  return useInfiniteQuery<PaginatedApiResponse<InventoryHistory>, Error>(
    queryKey,
    async ({ pageParam = defaultParams }) => {
      const searchParams = new URLSearchParams();
      searchParams.set('startIndex', pageParam.startIndex);
      searchParams.set('pageSize', pageParam.pageSize);

      const url = `/inventory/${id}/history?${searchParams}`;

      return defaultMetaQueryFn(url);
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage.links.next) {
          return;
        }

        const url = new URL(lastPage.links.next);
        const nextPageParams = Object.fromEntries(url.searchParams.entries());

        return nextPageParams;
      },
      enabled: !!id,
    }
  );
}

/**
 * @package api/useInventoryNotes
 * @description - Get notes collection as Note[] for specific inventory
 * @param id vehicle UUID
 */
export function useInventoryNotes(id: string) {
  const path = `/inventory/${id}/inventoryNotes`;
  return useQuery<APIResponse<NotesByDate[]>>([path], {
    enabled: !!id,
  });
}

/**
 * @package api/inventory
 * @description - Get recall information as Recall[] for specific inventory
 * @param id vehicle UUID
 */
export function useVehicleRecalls(id: string) {
  const path = `/inventory/${id}/recalls`;
  return useQuery<APIResponse<Recall[]>>([path], {
    enabled: !!id,
  });
}

/**
 * @package api/inventory/{vehicleId}/location
 * @description - Get location as GeoLocation for specific inventory
 * @param id vehicle UUID
 */
export function useVehicleLocation(id: string) {
  const path = `/inventory/${id}/location`;
  return useQuery<APIResponse<InventoryLocation[]>>([path], {
    enabled: !!id,
  });
}

/**
 * @package api/inventory/{vehicleId}/plugins/vdp/checklists
 * @description - Get checklists for specific inventory
 * @param id vehicle UUID
 */
export function useVehicleChecklists(vehicleId: string) {
  const path = `/inventory/${vehicleId}/plugins/vdp/checklists`;
  return useQuery<APIResponse<Checklist[]>>([path], {
    enabled: !!vehicleId,
  });
}

/**
 * @package api/inventory/{vehicleId}/plugins/vdp/checklists/{checklistId}
 * @description - Get checklist for specific inventory
 * @param vehicleId vehicle UUID
 * @param checklistId checklist UUID
 */
export function useVehicleChecklist(vehicleId: string, checklistId: string) {
  const path = `/inventory/${vehicleId}/plugins/vdp/checklists/${checklistId}`;
  return useQuery<APIResponse<CheckListInstance>>([path], {
    enabled: !!vehicleId && !!checklistId,
  });
}

/**
 * @package api/inventory/{vehicleId}/plugins/vdp/checklists/{checklistId}
 * @description - update answers for checklist for specific inventory
 * @param vehicleId vehicle UUID
 * @param checklistId checklist UUID
 */
export function useUpdateVehicleChecklist(
  vehicleId: string,
  checklistId: string
) {
  const queryClient = useQueryClient();
  const path = `/inventory/${vehicleId}/plugins/vdp/checklists/${checklistId}`;
  return useMutation((data) => defaultMutationFn(path, 'PUT', data), {
    onMutate: async (answers: any) => {
      await queryClient.cancelQueries([path]);
      const previousValue: any = queryClient.getQueryData([path]);

      queryClient.setQueryData([path], (data: any) => {
        data.data.answers = answers;
        return data;
      });

      return previousValue;
    },
    onError: () => {
      queryClient.invalidateQueries([path]);
    },
    onSettled: () => {
      queryClient.invalidateQueries([
        `/inventory/${vehicleId}/plugins/vdp/checklists`,
      ]);
    },
  });
}

/**
 * @package api/inventory
 * @description - Get summary for a vehicle
 * @param id vehicle UUID
 */
export function useVehicleSummary(id: string | undefined) {
  const path = `/inventory/${id}`;
  return useQuery<APIResponse<VehicleSummary>>([path], {
    enabled: !!id,
  });
}

/**
 * @package api/inventory
 * @description - Get summary for a vehicle
 * @param id vehicle UUID
 */
export function useVehicleSummaryByTenantId(
  inventoryId: string | undefined,
  tenantId: string | undefined
) {
  const path = `/inventory/${inventoryId}`;
  const { isVendor, isLoading } = useVendor();

  // TODO: find consistent solution for multi-tenant / vendors
  // vendor vehicle summary will come back without auth-tenant-id
  // multi-tenant requires auth-tenant-id
  return useQuery<APIResponse<VehicleSummary>>({
    enabled: !!inventoryId && !!tenantId && !isLoading,
    queryKey: [path, isVendor ? undefined : tenantId],
    queryFn: () =>
      defaultMetaQueryFn(path, { headers: { 'auth-tenant-id': tenantId } }),
  });
}

/**
 * @package api/inventory
 * @description - Get tenantId from inventory or current location tenantId
 * @param inventoryId inventory UUID
 * @param enabled boolean to enable or disable query
 */
export function useGetInventoryTenantId(
  inventoryId: string | undefined,
  enabled = true
) {
  const path = `/inventory/${inventoryId}/tenantId`;

  return useQuery<APIResponse<string>>([path], {
    enabled: !!inventoryId && !!enabled,
  });
}

export interface InventoryQuerySearchParams {
  sort?: string;
  sortDirection?: string;
  search?: string;
  filters: {
    assignee?: string;
    stepId?: string;
    tenant?: string;
    disposition?: string;
    area?: string;
    status?: string;
    age?: string;
    condition?: string;
    tag?: string;
    recalls?: string;
    inventoryState?: string;
    tasks?: string;
    color?: string;
    year?: string;
    retailPrice?: string;
    bodyType?: string;
    make?: string;
    model?: string;
    mileage?: string;
  };
}

export const FILTER_PARAM_NAMES = [
  'age',
  'area',
  'assignee',
  'bodyType',
  'condition',
  'color',
  'disposition',
  'inventoryState',
  'make',
  'model',
  'mileage',
  'recalls',
  'retailPrice',
  'status',
  'stepId',
  'tenant',
  'tag',
  'tasks',
  'year',
] as const;

const filterGetters = {
  age: () => {
    const queryParam = getLastQueryParamWithName('age') || '';
    return convertUiFilterToApiFilter(queryParam, `AGE`);
  },
  area: () => {
    const queryParam = getLastQueryParamWithName('area') || '';
    return convertUiFilterToApiFilter(queryParam, 'AREA');
  },
  assignee: () => {
    const queryParam = getLastQueryParamWithName('assignee') || '';
    return convertUiFilterToApiFilter(queryParam, 'ASSIGNEE');
  },
  bodyType: () => {
    const queryParam = getAllQueryParamsWithName('bodyType') || [];
    return convertUiFilterToApiFilter(queryParam, 'VEHICLE_BODY_TYPE');
  },
  condition: () => {
    const queryParam = getAllQueryParamsWithName('condition') || [];
    return convertUiFilterToApiFilter(queryParam, 'CONDITION');
  },
  color: () => {
    const queryParam = getAllQueryParamsWithName('color') || [];
    return convertUiFilterToApiFilter(queryParam, 'INVENTORY_COLOR');
  },
  disposition: () => {
    const queryParam = getAllQueryParamsWithName('disposition') || [];
    return convertUiFilterToApiFilter(queryParam, 'DISPOSITION');
  },
  inventoryState: () => {
    const queryParam = getLastQueryParamWithName('inventoryState') || '';
    return convertUiFilterToApiFilter(
      queryParam,
      'INVENTORY_STATE',
      workflowInventoryStatesUiToApiMap
    );
  },
  make: () => {
    const queryParam = getAllQueryParamsWithName('make') || [];
    return convertUiFilterToApiFilter(queryParam, 'VEHICLE_MAKE');
  },
  model: () => {
    const queryParam = getAllQueryParamsWithName('model') || [];
    return convertUiFilterToApiFilter(queryParam, 'VEHICLE_MODEL');
  },
  mileage: () => {
    const queryParam = getLastQueryParamWithName('mileage') || '';
    return convertUiFilterToApiFilter(queryParam, 'INVENTORY_MILEAGE');
  },
  recalls: () => {
    const queryParam = getLastQueryParamWithName('recalls') || '';
    return convertUiFilterToApiFilter(queryParam, 'RECALLS');
  },
  retailPrice: () => {
    const queryParam = getLastQueryParamWithName('retailPrice') || '';
    return convertUiFilterToApiFilter(queryParam, 'INVENTORY_RETAIL_PRICE');
  },
  status: () => {
    const queryParam = getLastQueryParamWithName('status') || '';
    if (queryParam === 'ALL') {
      return '';
    }
    return convertUiFilterToApiFilter(
      queryParam,
      'STEP_STATUS',
      workflowStatusesUiToApiMap
    );
  },
  stepId: () => {
    const queryParam = getLastQueryParamWithName('stepId') || '';
    return convertUiFilterToApiFilter(queryParam, 'STEP');
  },
  tenant: () => {
    const queryParam = getAllQueryParamsWithName('tenant') || [];
    return convertUiFilterToApiFilter(queryParam, 'TENANT');
  },
  tag: () => {
    const queryParam = getAllQueryParamsWithName('tag') || [];
    return convertUiFilterToApiFilter(queryParam, 'TAG');
  },
  tasks: () => {
    const queryParam = getLastQueryParamWithName('tasks') || '';
    return convertUiFilterToApiFilter(queryParam, 'TASKS');
  },
  year: () => {
    const queryParam = getLastQueryParamWithName('year') || '';
    return convertUiFilterToApiFilter(queryParam, 'VEHICLE_YEAR');
  },
};

const workflowStatusesUiToApiMap: Record<string, string> = {
  OK: 'ONTIME',
  WARNING: 'WARNING',
  DANGER: 'LATE',
  UNKNOWN: 'UNKNOWN',
  ALL: 'ALL',
};

const workflowInventoryStatesUiToApiMap: Record<string, string> = {
  INVENTORY_ONLY: 'INVENTORY_ONLY',
  INVENTORY_PLUS_SOLD: 'INVENTORY_PLUS_SOLD',
  IN_RECON: 'IN_RECON',
  MANUALLY_CREATED: 'MANUALLY_CREATED',
};

const convertUiFilterToApiFilter = (
  value: string | string[],
  apiFilterId: string,
  upMap?: Record<string, string>
) => {
  if (value) {
    if (upMap && typeof value === 'string') value = upMap[value];
    if (Array.isArray(value)) {
      const filterStrings = value.map((item) => `${apiFilterId}::${item}`);
      return filterStrings.join(',');
    }
    if (value) {
      return `${apiFilterId}::${value}`;
    }
    return '';
  }
};

export const getCurrentFilterParamsFromUrl = (): string => {
  const filters: string[] = [];
  FILTER_PARAM_NAMES.forEach((name) => {
    const value = filterGetters[name]();
    if (!value) {
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return;
      }
    }
    filters.push(value);
  });
  return filters.join(',');
};

export const useActiveFilters = (): { filter: string; value: any }[] => {
  const location = useLocation();
  return useMemo(() => {
    const filters: any = {};
    if (!location.search) {
      return [];
    }

    const currentFilters = getCurrentFilterParamsFromUrl();
    if (currentFilters.trim().length > 0) {
      const values = currentFilters.split(',');
      values.forEach((item) => {
        let [filter, value] = item.split('::');
        if (!filters[filter]) {
          filters[filter] = [value];
        } else {
          filters[filter].push(value);
        }
      });
    }

    return Object.keys(filters).map((filter) => {
      if (filters[filter].length > 1) {
        return { filter, value: filters[filter] };
      } else {
        return { filter, value: filters[filter][0] };
      }
    });
  }, [location.search]);
};

export const INVENTORY_PAGE_SIZE = 20;

export function getVehicleListParams() {
  const path = `/inventory`;
  const filterIds = getCurrentFilterParamsFromUrl();
  const searchString = getLastQueryParamWithName('s') || '';
  const sortId = getLastQueryParamWithName('sort') || 'AGE';
  const sortDirection =
    getLastQueryParamWithName('sortDirection') || 'DESCENDING';
  const queryKey = [path, filterIds, searchString, sortId, sortDirection];
  return {
    path,
    filterIds,
    searchString,
    sortId,
    sortDirection,
    queryKey,
  };
}

/**
 * @package api/inventory
 * @description - GET inventory list filtered by query object properties and paged by index.
 */
export function useVehicleList(startIndex: number = 0) {
  const { path, filterIds, searchString, sortId, sortDirection, queryKey } =
    getVehicleListParams();

  return useInfiniteQuery<APIResponse<VehicleSummary[]>>(
    queryKey,
    async ({ pageParam = { startIndex } }) => {
      const searchParams = new URLSearchParams();
      if (filterIds) {
        searchParams.set('filterIds', filterIds);
      }
      if (searchString) {
        searchParams.set('s', searchString);
      }
      searchParams.set('sortId', sortId);
      searchParams.set('sortDirection', sortDirection);
      searchParams.set('pageSize', `${INVENTORY_PAGE_SIZE}`);
      searchParams.set('startIndex', `${pageParam.startIndex}`);
      searchParams.set('includeTaskNotes', 'true');
      const url = `${path}?${searchParams.toString()}`;

      return defaultMetaQueryFn(url);
    },
    {
      refetchOnMount: false,
      staleTime: 1800000,
    }
  );
}

export function useCreateVehicle(isRecon: boolean) {
  const path = isRecon ? '/recon' : '/inventory';
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, CreateVehiclePayload>(
    (vehicle) => {
      return defaultMutationFn(path, 'POST', vehicle);
    },
    {
      onSuccess: async () => {
        if (isRecon) {
          queryClient.invalidateQueries(['/dashboard/goals']);
        }
      },
    }
  );
}

export function useUpdateVehicle(vehicleId?: string) {
  const path = `/inventory/${vehicleId}`;
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, CreateVehiclePayload>(
    (data) => defaultMutationFn(path, 'PUT', data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([`/inventory/${vehicleId}`]);
      },
    }
  );
}

export function useUpdateReconInfo(vehicleId?: string) {
  const path = `/inventory/${vehicleId}/reconInfo`;
  const queryClient = useQueryClient();

  return useMutation((data: any) => defaultMutationFn(path, 'PUT', data), {
    onSuccess: async () => {
      await queryClient.invalidateQueries([`/inventory/${vehicleId}`]);
    },
  });
}

export function useVehicleAttachments(id: string) {
  const path = `/inventory/${id}/attachments`;
  return useQuery<ApiResponse<VehicleAttachment[]>>([path]);
}

export function useVehicleMedia(id: string) {
  const path = `/inventory/${id}/media`;
  return useQuery<
    ApiResponse<{ images: VehicleImage[]; videos: VehicleImage[] }>
  >([path]);
}

export function useUpdateVehicleInfo(vehicleId?: string) {
  const path = `/recon/${vehicleId}/vehicleInfo`;
  const queryClient = useQueryClient();

  return useMutation(
    (vehicleData: Partial<VehicleCard>) =>
      defaultMutationFn(path, 'PUT', vehicleData),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([`/inventory/${vehicleId}`]);
      },
    }
  );
}

export function useVehicleRepairOrders(id: string) {
  const path = `/inventory/${id}/repairOrders`;
  return useQuery<ApiResponse<VehicleRepairOrder[]>>([path]);
}

export function useVehicleTags(
  vehicleId?: string,
  options?: { enabled?: boolean }
) {
  const path = `/inventory/${vehicleId}/tags`;
  const enabled = options?.enabled ?? true;
  return useQuery<ApiResponse<VehicleTag[]>>([path], {
    enabled: !!vehicleId && enabled,
  });
}

export function useAssignTags(vehicleId?: string) {
  const path = `/inventory/${vehicleId}/tags`;
  const queryClient = useQueryClient();

  return useMutation(
    (tags: VehicleTag[]) => {
      const tagIds = tags.map((tag) => tag.tagId);
      return defaultMutationFn(path, `PUT`, tagIds);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([`/inventory/${vehicleId}/tags`]);
      },
    }
  );
}

export function useUnassignTags(vehicleId?: string) {
  const path = `/inventory/${vehicleId}/tags`;
  const queryClient = useQueryClient();

  return useMutation(
    (tags: VehicleTag[]) => {
      const tagInstanceIds = tags.map((tag) => tag.instanceId);
      return defaultMutationFn(path, 'DELETE', tagInstanceIds);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([`/inventory/${vehicleId}/tags`]);
      },
    }
  );
}

export function useCreateAndAssignVehicleTag(vehicleId?: string) {
  const path = `/inventory/${vehicleId}/tags`;

  return useMutation((tag: VehicleTag) => defaultMutationFn(path, 'POST', tag));
}

export function usePrintVehicleInfoPDF() {
  return useMutation(({ vehicleId }: { vehicleId: string }) => {
    const displayAllSections =
      '?excludeNotes=false&excludeTasks=false&excludeHeader=false&excludeAppraisalInfo=false';
    const path = `/inventory/${vehicleId}/export/pdf${displayAllSections}`;
    return defaultMetaQueryFn(path);
  });
}

export function useBackToReconWorkflow() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ vehicleId }: { vehicleId: string }) => {
      const path = `/inventory/${vehicleId}/moveBackToRecon`;
      return defaultMutationFn(path, 'PUT');
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([`/inventory/${variables.vehicleId}`]);
        queryClient.invalidateQueries([`/assignments`]);
        // TODO invalidate inventory
      },
    }
  );
}

type PresignedAttachmentUploadUrlInput = {
  vehicleId: string;
  contentType: string;
};

export function usePresignedAttachmentUploadUrl() {
  const getNewPresignedAttachmentUploadUrl = async ({
    vehicleId,
    contentType,
  }: PresignedAttachmentUploadUrlInput) => {
    const path = `/inventory/${vehicleId}/attachments/upload`;

    const params = new URLSearchParams();
    params.set('contentType', contentType);

    const partialUrl = `${path}?${params}`;

    const response = await configuredFetch(partialUrl);
    const data: APIResponse<PresignedFileUpload> = await response.json();
    return data;
  };

  return {
    getNewPresignedAttachmentUploadUrl,
  };
}

type AddAttachmentToVehicleInput = {
  vehicleId: string;
  attachmentName: string;
  s3AttachmentId: string;
};

export function useAddAttachmentToVehicle() {
  const mutation = useMutation<
    APIResponse<Attachment>,
    unknown,
    AddAttachmentToVehicleInput
  >(({ vehicleId, attachmentName, s3AttachmentId }) => {
    const path = `/inventory/${vehicleId}/attachments`;

    const params = new URLSearchParams();
    params.set('attachmentName', attachmentName);
    params.set('s3AttachmentId', s3AttachmentId);

    const partialUrl = `${path}?${params}`;

    return defaultMutationFn(partialUrl, 'POST');
  });

  return {
    addAttachmentToVehicle: mutation.mutateAsync,
    ...mutation,
  };
}

type PresignedMediaUploadUrlInput = {
  vehicleId: string;
  contentType: string;
};

export function usePresignedMediaUploadUrl() {
  const getNewPresignedMediaUploadUrl = async ({
    vehicleId,
    contentType,
  }: PresignedMediaUploadUrlInput) => {
    const path = `/inventory/${vehicleId}/media/upload`;

    const params = new URLSearchParams();
    params.set('contentType', contentType);

    const partialUrl = `${path}?${params}`;

    const response = await configuredFetch(partialUrl);
    const data: APIResponse<PresignedFileUpload> = await response.json();
    return data;
  };

  return {
    getNewPresignedMediaUploadUrl,
  };
}

type AddMediaImageToVehicleInput = {
  vehicleId: string;
  attachmentName: string;
  s3AttachmentId: string;
  shot?: VehicleImageShots;
};

export function useAddMediaImageToVehicle() {
  const mutation = useMutation<
    APIResponse<Attachment>,
    unknown,
    AddMediaImageToVehicleInput
  >(({ vehicleId, attachmentName, s3AttachmentId, shot }) => {
    const path = `/inventory/${vehicleId}/media/images`;

    const params = new URLSearchParams();
    params.set('attachmentName', attachmentName);
    params.set('s3AttachmentId', s3AttachmentId);
    params.set('shot', shot ?? VehicleImageShots.UNKNOWN);

    const partialUrl = `${path}?${params}`;

    return defaultMutationFn(partialUrl, 'POST');
  });

  return {
    addMediaImageToVehicle: mutation.mutateAsync,
    ...mutation,
  };
}

type AddMediaVideoToVehicleInput = {
  vehicleId: string;
  attachmentName: string;
  s3AttachmentId: string;
};

export function useAddMediaVideoToVehicle() {
  const mutation = useMutation<
    APIResponse<Attachment>,
    unknown,
    AddMediaVideoToVehicleInput
  >(({ vehicleId, attachmentName, s3AttachmentId }) => {
    const path = `/inventory/${vehicleId}/media/videos`;

    const params = new URLSearchParams();
    params.set('attachmentName', attachmentName);
    params.set('s3AttachmentId', s3AttachmentId);

    const partialUrl = `${path}?${params}`;

    return defaultMutationFn(partialUrl, 'POST');
  });

  return {
    addMediaVideoToVehicle: mutation.mutateAsync,
    ...mutation,
  };
}

export function useInventoryPendingApproval() {
  const path = '/inventory/pendingApproval';
  return useQuery<ApiResponse<VehicleSummary[]>>([path]);
}

export function useInventoryTasks(
  statuses: TaskStatus[],
  assignees: string[],
  pageNumber: number = 0,
  pageSize: number = TASKS_PAGE_SIZE
) {
  const statusTaskFilterParams = convertUiFilterToApiFilter(
    statuses,
    'statusTaskFilter'
  );

  const assigneeTaskFilterParams = convertUiFilterToApiFilter(
    assignees,
    'assigneeTaskFilter'
  );

  const filterIds = `filterIds=${statusTaskFilterParams},${assigneeTaskFilterParams}`;
  const pageSizeParam = pageSize >= 0 ? `&pageSize=${pageSize}` : '';
  const startIndex = pageNumber * pageSize;
  const startIndexParam = startIndex >= 0 ? `&startIndex=${startIndex}` : '';

  const path = `/recon/dashboard/tasks?${filterIds}${startIndexParam}${pageSizeParam}`;
  return useQuery<PaginatedApiResponse<VehicleSummary[]>>([path]);
}
